import { HttpClient, HttpContextToken, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ADD_FORM_LOCALES, SKIP_API_PREFIX, SKIP_ERROR_HANDLER, SKIP_LANGUAGE, SKIP_REFRESH_TOKEN, SKIP_TOKEN, withSkips } from '../http-context';

// HttpClient is declared in a re-exported module, so we augment the original module to make it work
// properly (see https://github.com/Microsoft/TypeScript/issues/13897). This exposes the fluent
// builder methods on the HttpClient *type*, so call sites that `inject(HttpClient)` see them with no
// casting once HttpClient is DI-swapped for HttpService.
declare module '@angular/common/http' {
    export interface HttpClient {
        /** Skips the default error handler for this request. */
        skipErrorHandler(): HttpClient;
        /** Does not prepend the API prefix for this request. */
        skipApiPrefix(): HttpClient;
        /** Skips attaching `withCredentials` for this request. */
        skipToken(): HttpClient;
        /** Skips refresh-token handling on 401 for this request. */
        skipRefreshToken(): HttpClient;
        /** Skips the Accept-Language header for this request. */
        skipLanguage(): HttpClient;
        /** Activates the form-locales interceptor for this request. */
        addLocales(): HttpClient;
    }
}

/**
 * Immutable fluent HttpClient. Each `.skip*()` / `.add*()` returns a *new*
 * instance sharing the same {@link HttpHandler} with one more context token in
 * its set; `request()` stamps those tokens onto the request's `HttpContext` and
 * delegates to the real Angular pipeline via `super.request()`. Concurrent
 * callers cannot pollute each other.
 */
class ConfigurableHttpClient extends HttpClient {
    constructor(
        private readonly httpHandler: HttpHandler,
        private readonly tokens: ReadonlySet<HttpContextToken<boolean>>,
    ) {
        super(httpHandler);
    }

    override skipErrorHandler(): HttpClient {
        return this.with(SKIP_ERROR_HANDLER);
    }

    override skipApiPrefix(): HttpClient {
        return this.with(SKIP_API_PREFIX);
    }

    override skipToken(): HttpClient {
        return this.with(SKIP_TOKEN);
    }

    override skipRefreshToken(): HttpClient {
        return this.with(SKIP_REFRESH_TOKEN);
    }

    override skipLanguage(): HttpClient {
        return this.with(SKIP_LANGUAGE);
    }

    override addLocales(): HttpClient {
        return this.with(ADD_FORM_LOCALES);
    }

    // Single override point: every verb (get/post/put/delete/patch/head/options) funnels through
    // request(), so stamping the context here covers both the (method, url, options) form and the
    // HttpRequest form. Calls the genuine Angular HttpClient pipeline (super.request()).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    override request(method: any, url?: any, options: any = {}): Observable<any> {
        if (method instanceof HttpRequest) {
            return super.request(method.clone({ context: withSkips(this.tokens, method.context) }));
        }
        return super.request(method, url, { ...options, context: withSkips(this.tokens, options.context) });
    }

    private with(token: HttpContextToken<boolean>): ConfigurableHttpClient {
        return new ConfigurableHttpClient(this.httpHandler, new Set(this.tokens).add(token));
    }
}

/**
 * Injectable entry point. Registered via the explicit
 * `{ provide: HttpClient, useClass: HttpService }` swap in `app.config.ts` —
 * no `providedIn`.
 */
@Injectable()
export class HttpService extends ConfigurableHttpClient {
    constructor() {
        super(inject(HttpHandler), new Set());
    }
}
